import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FileText, Plus, Loader2, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { bobAnalyseService } from '@/lib/services/bobAnalyseService'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { useToast } from '@/lib/hooks/useToast'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { Badge } from '@/components/ui/badge'
import { formatDateTime } from '@/lib/utils/timeFormat'
import type { AvailableScan } from '@/lib/services/bobAnalyseService'

interface AddSourcesPanelProps {
  bobAnalyseId: string
  onSourceAdded?: () => void
}

export function AddSourcesPanel({ bobAnalyseId, onSourceAdded }: AddSourcesPanelProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { activeCrisis } = useCrisisStore()
  const canUpdateBob = useHasPermission('bob.update')
  const isClosedCrisis = activeCrisis?.status === 'closed'

  // Panel state - collapsed by default for compact UI
  const [isOpen, setIsOpen] = useState(true)
  const [selectedScanId, setSelectedScanId] = useState<string>('')
  const [customInputLabel, setCustomInputLabel] = useState<string>('')
  const [customInputContent, setCustomInputContent] = useState<string>('')
  const [activeTab, setActiveTab] = useState<'scan' | 'custom'>('scan')

  // Fetch available scans that haven't been used for this BOB yet
  const { data: availableScans, isLoading: scansLoading } = useQuery({
    queryKey: ['available-scans-for-bob', bobAnalyseId],
    queryFn: () => bobAnalyseService.getAvailableScansForBob(bobAnalyseId),
    enabled: !!bobAnalyseId && !!activeCrisis,
  })

  // Fetch suggestions to check if there are pending ones
  const { data: suggestions = [] } = useQuery({
    queryKey: ['bob-suggestions', bobAnalyseId, activeCrisis?.id],
    queryFn: () => bobAnalyseService.getSuggestions(bobAnalyseId),
    enabled: !!bobAnalyseId && !!activeCrisis,
  })

  // Check if there are ready suggestions that need to be processed
  const hasReadySuggestions = suggestions.some(s => s.status === 'ready')

  // Find the newest scan
  const newestScan = availableScans?.reduce((newest, scan) => {
    if (!newest || !scan.createdAt) return scan
    if (!newest.createdAt) return scan
    return new Date(scan.createdAt) > new Date(newest.createdAt) ? scan : newest
  }, null as AvailableScan | null)

  // Add source mutation
  const addSourceMutation = useMutation({
    mutationFn: (data: { scanId?: string; customInput?: { label: string; text: string } }) =>
      bobAnalyseService.addSource(bobAnalyseId, data),
    onSuccess: () => {
      toast({
        title: 'Bron toegevoegd',
        description: 'De bron wordt nu verwerkt om suggesties te genereren.',
      })
      // Reset form
      setSelectedScanId('')
      setCustomInputLabel('')
      setCustomInputContent('')
      // Invalidate queries to refetch suggestions and available scans
      queryClient.invalidateQueries({ queryKey: ['available-scans-for-bob', bobAnalyseId] })
      queryClient.invalidateQueries({ queryKey: ['bob-suggestions', bobAnalyseId] })
      onSourceAdded?.()
    },
    onError: error => {
      console.error('Error adding source:', error)
      toast({
        title: 'Fout bij toevoegen bron',
        description: 'Er is een fout opgetreden. Probeer het opnieuw.',
        variant: 'destructive',
      })
    },
  })

  const handleAddScan = () => {
    if (!selectedScanId) return
    addSourceMutation.mutate({ scanId: selectedScanId })
  }

  const handleAddCustomInput = () => {
    if (!customInputLabel.trim() || !customInputContent.trim()) return
    addSourceMutation.mutate({
      customInput: {
        label: customInputLabel.trim(),
        text: customInputContent.trim(),
      },
    })
  }

  // Don't show if user can't update or crisis is closed
  if (!canUpdateBob || isClosedCrisis) {
    return null
  }

  const hasAvailableScans = availableScans && availableScans.length > 0
  const canAddScan = selectedScanId && !addSourceMutation.isPending && !hasReadySuggestions
  const canAddCustom =
    customInputLabel.trim() && customInputContent.trim() && !addSourceMutation.isPending && !hasReadySuggestions

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Plus className="w-4 h-4 text-orange-500" />
          <span className="font-medium text-sm">Bronnen toevoegen</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {isOpen && (
        <div className="px-3 pb-3 space-y-3">
          {hasReadySuggestions ? (
            <div className="rounded p-3">
              <p className="text-sm text-orange-800 font-medium">
                Verwerk eerst de bestaande suggesties voordat je nieuwe bronnen toevoegt.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Voeg een omgevingsbeeld of eigen input toe om AI-suggesties te genereren.
              </p>


              {/* Tab buttons */}
              <div className="flex gap-1 border-b border-gray-200 pb-1 mt-6">
                <button
                  type="button"
                  onClick={() => setActiveTab('scan')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${activeTab === 'scan'
                    ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  Omgevingsbeeld
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('custom')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${activeTab === 'custom'
                    ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  Eigen invoer
                </button>
              </div>

              {/* Scan selection tab */}
              {activeTab === 'scan' && (
                <div className="space-y-2">
                  {scansLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Omgevingsbeelden laden...</span>
                    </div>
                  ) : !hasAvailableScans ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                      <FileText className="w-3 h-3" />
                      <span>Geen omgevingsbeelden beschikbaar.</span>
                    </div>
                  ) : (
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Select value={selectedScanId} onValueChange={setSelectedScanId}>
                          <SelectTrigger className="w-full h-8 text-sm">
                            <SelectValue placeholder="Selecteer omgevingsbeeld" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableScans.map((scan: AvailableScan) => {
                              const createdDate = scan.createdAt
                                ? formatDateTime(new Date(scan.createdAt))
                                : 'Onbekende datum'
                              const isNewest = newestScan?.id === scan.id
                              return (
                                <SelectItem key={scan.id} value={scan.id}>
                                  <div className="flex items-center justify-between w-full">
                                    <span className="text-sm">{createdDate}</span>
                                    {isNewest && (
                                      <Badge variant="secondary" className="ml-2 text-xs">
                                        Nieuwste
                                      </Badge>
                                    )}
                                  </div>
                                </SelectItem>
                              )
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleAddScan}
                        disabled={!canAddScan}
                        size="sm"
                        className="h-8 text-sm"
                        title={hasReadySuggestions ? 'Verwerk eerst bestaande suggesties' : ''}
                      >
                        {addSourceMutation.isPending ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Plus className="w-3 h-3 mr-1" />
                            Toevoegen
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Custom input tab */}
              {activeTab === 'custom' && (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label htmlFor="custom-label" className="text-sm">Label</Label>
                    <Input
                      id="custom-label"
                      value={customInputLabel}
                      onChange={e => setCustomInputLabel(e.target.value)}
                      placeholder="Bijv. LCMS, Persconferentie"
                      maxLength={100}
                      className="h-8 text-sm"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="custom-content" className="text-sm">Inhoud</Label>
                    <Textarea
                      id="custom-content"
                      value={customInputContent}
                      onChange={e => setCustomInputContent(e.target.value)}
                      placeholder="Plak hier de tekst..."
                      rows={3}
                      className="resize-y text-sm"
                    />
                  </div>

                  <Button
                    onClick={handleAddCustomInput}
                    disabled={!canAddCustom}
                    size="sm"
                    className="h-8 text-sm"
                    title={hasReadySuggestions ? 'Verwerk eerst bestaande suggesties' : ''}
                  >
                    {addSourceMutation.isPending ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-3 h-3 mr-1" />
                        Toevoegen
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
